import { CartLinesUpdateEvent } from '@shopify/events';

if (!customElements.get('ks-product-card')) {
  customElements.define('ks-product-card', class extends HTMLElement {
    connectedCallback() {
      this.primaryImage = this.querySelector('.ks-product-card__image--primary');
      this.secondaryImage = this.querySelector('.ks-product-card__image--secondary');
      this.currentPrice = this.querySelector('.ks-product-card__current-price');
      this.comparePrice = this.querySelector('.ks-product-card__compare-price');
      this.savings = this.querySelector('.ks-product-card__savings');
      this.quickButton = this.querySelector('[data-ks-quick-trigger]');
      this.panel = this.querySelector('[data-ks-quick-panel]');
      this.feedback = this.querySelector('[data-ks-feedback]');
      this.colorIndex = Number(this.dataset.colorIndex ?? -1);
      this.sizeIndex = Number(this.dataset.sizeIndex ?? -1);
      this.selectedColor = this.querySelector('[data-ks-swatch].is-selected')?.dataset.value || this.dataset.selectedColor || '';
      const variantData = this.querySelector('[data-ks-variants]');
      this.variants = variantData ? JSON.parse(variantData.textContent) : [];

      this.formatMoney(this.currentPrice, this.currentPrice?.textContent.trim() || '');
      if (this.comparePrice && !this.comparePrice.hidden) {
        this.formatMoney(this.comparePrice, this.comparePrice.textContent.trim());
      }

      this.querySelectorAll('[data-ks-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => this.selectSwatch(swatch));
      });

      this.quickButton?.addEventListener('click', () => this.openQuickAdd());
      this.querySelector('[data-ks-quick-close]')?.addEventListener('click', () => this.closeQuickAdd());
      this.querySelectorAll('[data-ks-size]').forEach((button) => {
        button.addEventListener('click', () => this.addSelectedSize(button.dataset.value));
      });
      this.querySelector('[data-ks-single-add]')?.addEventListener('click', () => this.addSelectedSize(''));
    }

    formatMoney(element, value) {
      if (!element) return;
      const match = value.match(/^(.*?)([.,]\d{2})$/);
      element.textContent = match ? match[1] : value;
      if (match) {
        const decimals = document.createElement('span');
        decimals.className = 'ks-product-card__price-decimals';
        decimals.textContent = match[2];
        element.append(decimals);
      }
    }

    selectSwatch(swatch) {
      this.querySelectorAll('[data-ks-swatch]').forEach((button) => {
        const selected = button === swatch;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      this.selectedColor = swatch.dataset.value || '';

      if (this.primaryImage && swatch.dataset.image) {
        this.primaryImage.removeAttribute('srcset');
        this.primaryImage.src = swatch.dataset.image;
        this.primaryImage.alt = `${this.dataset.productTitle || 'Product'} — ${this.selectedColor}`;
        if (this.secondaryImage) {
          this.secondaryImage.hidden = true;
          this.secondaryImage.style.setProperty('display', 'none', 'important');
          this.secondaryImage.style.setProperty('opacity', '0', 'important');
          this.secondaryImage.style.setProperty('pointer-events', 'none', 'important');
        }
      }

      if (swatch.dataset.url) {
        this.querySelectorAll('[data-ks-product-link]').forEach((link) => {
          link.href = swatch.dataset.url;
        });
      }

      if (this.currentPrice && swatch.dataset.price) {
        this.formatMoney(this.currentPrice, swatch.dataset.price);
      }

      if (this.comparePrice) {
        const comparePrice = swatch.dataset.comparePrice || '';
        this.formatMoney(this.comparePrice, comparePrice);
        this.comparePrice.hidden = !comparePrice;
      }

      if (this.savings) {
        const savings = swatch.dataset.savings || '';
        this.savings.textContent = savings;
        this.savings.hidden = !savings;
      }
    }

    openQuickAdd() {
      if (!this.panel) return;
      if (this.sizeIndex < 0) {
        this.addSelectedSize('');
        return;
      }
      this.panel.hidden = !this.panel.hidden;
    }

    closeQuickAdd() {
      if (this.panel) this.panel.hidden = true;
    }

    findVariant(sizeValue) {
      return this.variants.find((variant) => {
        const colourMatches = this.colorIndex < 0 || !this.selectedColor || variant.options?.[this.colorIndex] === this.selectedColor;
        const sizeMatches = this.sizeIndex < 0 || !sizeValue || variant.options?.[this.sizeIndex] === sizeValue;
        return colourMatches && sizeMatches && variant.available;
      });
    }

    async addSelectedSize(sizeValue) {
      const variant = this.findVariant(sizeValue);
      if (!variant) {
        this.setFeedback('This option is sold out.');
        return;
      }

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = Array.from(cartItemsComponents)
        .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
        .filter(Boolean);

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );

      this.setBusy(true);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const responseData = await response.json();
        if (!response.ok) {
          throw new Error(responseData.description || responseData.message || 'Unable to add this item.');
        }

        const cartResponse = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('Unable to refresh the cart.');
        const cart = await cartResponse.json();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections: responseData.sections || {},
            items: cart.items,
            itemCount: cart.item_count,
            source: 'ks-product-card',
            sourceId: this.dataset.productTitle || '',
            didError: false,
          },
        });

        this.setFeedback('Added to cart.');
        this.quickButton.textContent = 'Added';
        setTimeout(() => {
          this.quickButton.textContent = 'Quick add';
          this.closeQuickAdd();
        }, 1200);
      } catch (error) {
        deferredEventPromise.reject(error);
        this.setFeedback(error.message || 'Please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (this.quickButton) this.quickButton.disabled = busy;
      this.querySelectorAll('[data-ks-size], [data-ks-single-add]').forEach((button) => {
        button.disabled = busy;
      });
    }

    setFeedback(message) {
      if (this.feedback) this.feedback.textContent = message;
    }
  });
}
